<template>
  <div ref="rootRef" class="relative">
    <ClientOnly>
      <!-- LOADING STATE  -->
      <div
        class="w-10 h-10 bg-gray-100 animate-pulse rounded-full"
        v-if="loading"
      ></div>
      <div v-else>
        <!-- NOT AUTHENTICATED -->
        <NuxtLink v-if="!user" :to="$localePath('/auth/login')">
          <BaseButton
            :label="$t('nav.user.login')"
            variant="outline"
            rounded="rounded-full"
            size="sm"
          />
        </NuxtLink>

        <!-- AUTHENTICATED -->
        <button
          v-else
          class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 cursor-pointer overflow-hidden border border-gray-200"
          @click="toggle"
        >
          <BaseImage
            @contextmenu.prevent
            @dragstart.prevent
            img-class="w-full h-full  object-cover"
            :uri="user.avatar"
            icon-class="h-6"
            v-if="user.avatar"
            :alt="
              `${user.name || ''} ${user.last_name || ''}` || 'Medium.am User'
            "
          />

          <BaseImage
            @contextmenu.prevent
            @dragstart.prevent
            v-else
            img-class="w-full h-full object-cover"
            uri="/no_avatar.jpg"
            icon-class="h-6"
            alt="Placeholder avatar"
          />
        </button>
      </div>
    </ClientOnly>

    <!-- DROPDOWN -->
    <Transition enter-active-class="fadeIn" leave-active-class="fadeOut">
      <div
        v-if="open && user"
        class="absolute shadow-xl right-0 top-13 w-80 rounded-lg bg-white border border-gray-200 z-50"
      >
        <div class="p-5 border-b border-b-gray-100">
          <div class="font-medium text-2xl leading-tight">
            {{ user.name }} {{ user.last_name }}
          </div>

          <div class="text-base truncate">
            {{ user.email }}
          </div>
        </div>

        <nav class="flex flex-col p-2">
          <button
            class="cursor-pointer flex items-center space-x-2 px-3 py-2 rounded hover:bg-gray-100"
            @click="navigate(item)"
            type="button"
            v-for="item in navItems"
            :key="item.title"
          >
            <Icon :name="item.icon" size="22" />
            <span>{{ $t(`nav.links.${item.key}`) }}</span>
          </button>
        </nav>
        <div class="w-full px-5 pb-5">
          <BaseButton
            @click="store.signoutUser"
            :loading="store.signinOut"
            :disabled="store.signinOut"
            variant="secondary"
            :label="$t('nav.user.logout')"
            class="w-full"
            size="sm"
          >
            <template>
              <Icon name="heroicons:arrow-right" size="24" />
            </template>
          </BaseButton>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import type { MenuItem } from "~~/types/global";
import type { AppUser } from "~~/types/user";

const store = useAuthStore();
const newArt = useNewArtworkStore();
const localePath = useLocalePath();

const user = useState<AppUser | null>("auth-user");
const loading = useState<AppUser | null>("auth-loading");

const open = ref<boolean>(false);
const rootRef = ref<HTMLElement | null>(null);
const navItems = shallowRef<MenuItem[]>([
  {
    id: 0,
    title: "Profile",
    path: "/dashboard",
    key: "profile",
    icon: "heroicons:user",
  },
  {
    id: 55,
    title: "Favorites",
    key: "favorites",
    path: "/dashboard/favorites",
    icon: "heroicons:heart",
  },
  {
    id: 1,
    title: "Gallery",
    path: "/gallery",
    key: "gallery",
    icon: "heroicons:photo",
  },
  {
    id: 2,
    title: "My Artworks",
    key: "my_artworks",
    path: "/dashboard/my-artworks",
    icon: "heroicons:paint-brush",
  },
  {
    id: 3,
    title: "New Artwork",
    key: "new_artwork",
    path: "/",
    icon: "heroicons:plus-circle",
  },
]);

const navigate = async (item: MenuItem) => {
  if (item.id === 3) {
    newArt.openNewArtwork();
    open.value = false;
    return;
  } else {
    const destination = item.path;
    await navigateTo(localePath(destination));
    open.value = false;
  }
};

const toggle = () => (open.value = !open.value);
onClickOutside(rootRef, () => (open.value = false));
</script>
